import { useState } from "react";
import { useApp } from "../lib/store";
import { API } from "../lib/api";
import { Mail, Download, PlugZap, MousePointerClick, CheckCircle2, Copy, Check } from "lucide-react";

const manifestUrl = `${API}/outlook/manifest.xml`;

function Step({ n, icon: Icon, title, children }) {
  return (
    <div className="flex gap-4" data-testid={`outlook-step-${n}`}>
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1B2A4A] text-[#F5A623] flex items-center justify-center font-bold">
        {n}
      </div>
      <div className="pt-1">
        <div className="flex items-center gap-2 font-semibold text-[#1B2A4A]">
          <Icon className="h-4 w-4" strokeWidth={1.75} /> {title}
        </div>
        <div className="text-sm text-gray-600 mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

export default function OutlookAddin() {
  const { t } = useApp();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(manifestUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) {}
  };

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="outlook-addin-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-[#1B2A4A] text-white p-8 mb-8">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#F5A623]/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#F5A623] text-[#1B2A4A] flex items-center justify-center">
            <Mail className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t("outlook_addin_title")}</h1>
            <p className="text-white/70 text-sm mt-1 max-w-xl">{t("outlook_addin_intro")}</p>
          </div>
        </div>
      </div>

      {/* Download */}
      <div className="rounded-xl border bg-white p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <div className="font-semibold text-[#1B2A4A]">{t("outlook_addin_manifest")}</div>
          <div className="text-xs text-gray-500 mt-1 break-all font-mono">{manifestUrl}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} data-testid="outlook-copy-url"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium text-[#1B2A4A] hover:bg-gray-50">
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            {copied ? t("copied") : t("copy")}
          </button>
          <a href={manifestUrl} download data-testid="outlook-download-manifest"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F5A623] text-[#1B2A4A] text-sm font-bold hover:brightness-95">
            <Download className="h-4 w-4" /> {t("outlook_addin_download")}
          </a>
        </div>
      </div>

      {/* Steps */}
      <div className="rounded-xl border bg-white p-6 space-y-6">
        <Step n={1} icon={Download} title={t("outlook_addin_s1_t")}>{t("outlook_addin_s1_d")}</Step>
        <Step n={2} icon={PlugZap} title={t("outlook_addin_s2_t")}>{t("outlook_addin_s2_d")}</Step>
        <Step n={3} icon={MousePointerClick} title={t("outlook_addin_s3_t")}>{t("outlook_addin_s3_d")}</Step>
        <Step n={4} icon={CheckCircle2} title={t("outlook_addin_s4_t")}>{t("outlook_addin_s4_d")}</Step>
      </div>

      <div className="text-xs text-gray-500 mt-4 leading-relaxed" data-testid="outlook-addin-note">
        {t("outlook_addin_note")}
      </div>
    </div>
  );
}
